import { useState } from 'react'
import { router } from '@inertiajs/react'
import {
  Download,
  Link2,
  Plus,
  Copy,
  Check,
  X,
  ExternalLink,
} from 'lucide-react'
import AppLayout from '@/layouts/AppLayout'
import BalanceCard from '@/components/BalanceCard'
import StatusBadge from '@/components/StatusBadge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { formatCurrency, formatDate } from '@/lib/utils'
import type { PurchaseOrder, Payment, Adjustment } from '@/types'

interface ShareableToken {
  id: number
  token: string
  active: boolean
  created_at: string
}

interface Props {
  balance: string
  outstanding_estimates: string
  total_payments: string
  total_finalized: string
  net_adjustments: string
  purchase_orders: PurchaseOrder[]
  payments: Payment[]
  adjustments: Adjustment[]
  tokens: ShareableToken[]
}

export default function StatementIndex({
  balance,
  outstanding_estimates,
  total_payments,
  total_finalized,
  net_adjustments,
  purchase_orders,
  payments,
  adjustments,
  tokens,
}: Props) {
  const [copiedId, setCopiedId] = useState<number | null>(null)

  function handleGenerateLink() {
    router.post('/shareable_tokens')
  }

  function handleDeactivate(id: number) {
    router.delete(`/shareable_tokens/${id}`)
  }

  function copyToClipboard(token: string, id: number) {
    const url = `${window.location.origin}/s/${token}`
    navigator.clipboard.writeText(url)
    setCopiedId(id)
    setTimeout(() => setCopiedId(null), 2000)
  }

  return (
    <AppLayout>
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">Statement</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Full account statement and sharing
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button asChild variant="outline" size="sm">
            <a href="/statement/csv" download>
              <Download className="size-4" />
              CSV
            </a>
          </Button>
          <Button asChild variant="outline" size="sm">
            <a href="/statement/pdf" target="_blank">
              <Download className="size-4" />
              PDF
            </a>
          </Button>
        </div>
      </div>

      {/* Balance summary */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5 mb-8">
        <Card className="border-border/50">
          <CardContent className="pt-6">
            <p className="text-xs font-medium text-muted-foreground">Balance</p>
            <p className={`text-2xl font-bold font-mono mt-1 ${parseFloat(balance) >= 0 ? 'text-positive' : 'text-destructive'}`}>
              {formatCurrency(parseFloat(balance))}
            </p>
          </CardContent>
        </Card>
        <Card className="border-border/50">
          <CardContent className="pt-6">
            <p className="text-xs font-medium text-muted-foreground">Outstanding Estimates</p>
            <p className="text-2xl font-bold font-mono mt-1 text-warning">
              {formatCurrency(parseFloat(outstanding_estimates))}
            </p>
          </CardContent>
        </Card>
        <Card className="border-border/50">
          <CardContent className="pt-6">
            <p className="text-xs font-medium text-muted-foreground">Total Payments</p>
            <p className="text-2xl font-bold font-mono mt-1 text-foreground">
              {formatCurrency(parseFloat(total_payments))}
            </p>
          </CardContent>
        </Card>
        <Card className="border-border/50">
          <CardContent className="pt-6">
            <p className="text-xs font-medium text-muted-foreground">Total Finalized</p>
            <p className="text-2xl font-bold font-mono mt-1 text-foreground">
              {formatCurrency(parseFloat(total_finalized))}
            </p>
          </CardContent>
        </Card>
        <Card className="border-border/50">
          <CardContent className="pt-6">
            <p className="text-xs font-medium text-muted-foreground">Net Adjustments</p>
            <p className="text-2xl font-bold font-mono mt-1 text-foreground">
              {formatCurrency(parseFloat(net_adjustments))}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Finalized POs */}
      <Card className="border-border/50 mb-6">
        <CardHeader>
          <CardTitle className="text-base">Finalized Purchase Orders</CardTitle>
        </CardHeader>
        <CardContent>
          {purchase_orders.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">
              No finalized purchase orders
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-border/50 hover:bg-transparent">
                  <TableHead className="text-xs text-muted-foreground">ID</TableHead>
                  <TableHead className="text-xs text-muted-foreground">Amount</TableHead>
                  <TableHead className="text-xs text-muted-foreground">Status</TableHead>
                  <TableHead className="text-xs text-muted-foreground">Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {purchase_orders.map((po) => (
                  <TableRow key={po.id} className="border-border/30">
                    <TableCell className="font-medium">#{po.id}</TableCell>
                    <TableCell className="font-mono text-sm">
                      {formatCurrency(parseFloat(po.amount))}
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={po.status} />
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDate(po.date)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Payments */}
      <Card className="border-border/50 mb-6">
        <CardHeader>
          <CardTitle className="text-base">All Payments</CardTitle>
        </CardHeader>
        <CardContent>
          {payments.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">
              No payments recorded
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-border/50 hover:bg-transparent">
                  <TableHead className="text-xs text-muted-foreground">Amount</TableHead>
                  <TableHead className="text-xs text-muted-foreground">Date</TableHead>
                  <TableHead className="text-xs text-muted-foreground">Linked PO</TableHead>
                  <TableHead className="text-xs text-muted-foreground">Notes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payments.map((payment) => (
                  <TableRow key={payment.id} className="border-border/30">
                    <TableCell className="font-mono text-sm">
                      {formatCurrency(parseFloat(payment.amount))}
                    </TableCell>
                    <TableCell className="text-sm">
                      {formatDate(payment.date)}
                    </TableCell>
                    <TableCell className="text-sm">
                      {payment.purchase_order_id
                        ? `PO #${payment.purchase_order_id}`
                        : '--'}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">
                      {payment.notes || '--'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Adjustments */}
      <Card className="border-border/50 mb-6">
        <CardHeader>
          <CardTitle className="text-base">Adjustments</CardTitle>
        </CardHeader>
        <CardContent>
          {adjustments.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">
              No adjustments recorded
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-border/50 hover:bg-transparent">
                  <TableHead className="text-xs text-muted-foreground">Type</TableHead>
                  <TableHead className="text-xs text-muted-foreground">Amount</TableHead>
                  <TableHead className="text-xs text-muted-foreground">Date</TableHead>
                  <TableHead className="text-xs text-muted-foreground">Linked PO</TableHead>
                  <TableHead className="text-xs text-muted-foreground">Notes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {adjustments.map((adjustment) => (
                  <TableRow key={adjustment.id} className="border-border/30">
                    <TableCell>
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                          adjustment.adjustment_type === 'credit'
                            ? 'bg-positive/10 text-positive'
                            : 'bg-destructive/10 text-destructive'
                        }`}
                      >
                        {adjustment.adjustment_type === 'credit' ? 'Credit' : 'Debit'}
                      </span>
                    </TableCell>
                    <TableCell className="font-mono text-sm">
                      {formatCurrency(parseFloat(adjustment.amount))}
                    </TableCell>
                    <TableCell className="text-sm">
                      {formatDate(adjustment.date)}
                    </TableCell>
                    <TableCell className="text-sm">
                      {adjustment.purchase_order_id
                        ? `PO #${adjustment.purchase_order_id}`
                        : '--'}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">
                      {adjustment.notes || '--'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Shareable links */}
      <Card className="border-border/50">
        <CardHeader className="flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            <Link2 className="size-4 text-muted-foreground" />
            Shareable Links
          </CardTitle>
          <Button size="sm" onClick={handleGenerateLink}>
            <Plus className="size-4" />
            Generate Link
          </Button>
        </CardHeader>
        <CardContent>
          {tokens.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">
              No shareable links generated yet
            </p>
          ) : (
            <div className="space-y-2">
              {tokens.map((token) => (
                <div
                  key={token.id}
                  className="flex items-center gap-3 rounded-lg border border-border/50 bg-muted/20 px-4 py-3"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-mono text-foreground truncate">
                      {window.location.origin}/s/{token.token}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Created {formatDate(token.created_at)}
                      {!token.active && (
                        <span className="ml-2 text-destructive">(Deactivated)</span>
                      )}
                    </p>
                  </div>

                  {token.active && (
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon-xs"
                        onClick={() => copyToClipboard(token.token, token.id)}
                      >
                        {copiedId === token.id ? (
                          <Check className="size-3.5 text-positive" />
                        ) : (
                          <Copy className="size-3.5" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon-xs"
                        onClick={() => handleDeactivate(token.id)}
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                      >
                        <X className="size-3.5" />
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </AppLayout>
  )
}
